import React, { useState } from 'react';
import { Button, Row } from 'components';
import { useItemSelectorListQuery, usePurchaseRequestByIdQuery, usePurchaseRequestItemsQuery } from 'api';
import { PurchaseRequestItemsTable } from './PurchaseRequestItemsTable';
import { PurchaseRequestItemsProps } from './types';
import styles from './styles.module.scss';
import { Skeleton } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';

export const PurchaseRequestItems: React.FC<PurchaseRequestItemsProps> = ({ purchaseRequestId }) => {
  const { id } = useParams() as { id: string };
  const navigate = useNavigate();
  const { data: purchaseRequestDetails = {} } = usePurchaseRequestByIdQuery({ id });
  const [selectedItems, setSelectedItems] = useState<React.Key[]>([]);
  const { data, isLoading } = usePurchaseRequestItemsQuery({ purchaseRequestId }, { skip: !purchaseRequestId });
  const { data: itemSelectorResponse } = useItemSelectorListQuery(
    { brandId: purchaseRequestDetails.purchaseRequest?.brand?.id as string },
    { skip: !purchaseRequestDetails.purchaseRequest?.brand?.id }
  );

  // Extract items from the response
  const apiItems = data || [];

  // Get item selector data
  const itemSelectorData = itemSelectorResponse || {};

  // Manually format items to match what the table expects
  const items = apiItems.map((item: any) => ({
    ...item,
    id: item.id || '',
    brandId: item.brandId,
    internalItemId: item.internalItemId || '',
    sku: item.sku || '',
    description: item.description || '',
    quantity: Number(item.quantity) || 0,
    // Handle both priceRange and priceRage (API typo)
    priceRange: item.priceRange || item.priceRage || null,
    // Set isSystemItem based on identified field
    isSystemItem: item.identified === true,
  }));

  const handleSelectionChange = (selectedRowKeys: React.Key[]) => {
    setSelectedItems(selectedRowKeys);
  };

  const handleRequestPrices = () => {
    // Navigate to the request price page with the selected items as query parameters
    navigate(`/admin/purchase-request/${id}/request-price?items=${selectedItems.join(',')}`);
  };

  return (
    <div className={styles.items_container}>
      <Row className={styles.actions_row} justify="end">
        <Button
          type="primary"
          disabled={!selectedItems.length}
          onClick={handleRequestPrices}
          className={styles.request_prices_btn}
        >
          Request Prices {selectedItems.length ? `(${selectedItems.length})` : ''}
        </Button>
      </Row>

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '24px' }}>
          <Skeleton active />
        </div>
      ) : (
        <PurchaseRequestItemsTable
          purchaseRequestId={purchaseRequestId}
          initialItems={items}
          loading={isLoading}
          brandId={purchaseRequestDetails.purchaseRequest?.brand?.id}
          onSelectionChange={handleSelectionChange}
          itemSelectorData={itemSelectorData}
        />
      )}
    </div>
  );
};
